import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getResume,
  updateResume,
  normalizeResumeSections,
  extractConversationAchievements,
  importResumeFromPdf,
  LocationStyle,
  Resume as ResumeType,
  Achievement,
  ResumeCritiqueSuggestion,
  ResumeData,
  ResumeExperience,
  ResumeEducation,
  ResumeProject,
  ResumeSkillEntry,
  SkillProficiency,
} from '../../services/resume';
import { ResumeCritique } from './ResumeCritique';
import { exportResumeToPDF } from '../../services/pdfExport';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

import './Resume.css';

type YearMonthParts = { year: string; month: string };

function parseYearMonth(value?: string): YearMonthParts {
  if (!value) return { year: '', month: '' };
  const match = value.match(/^(\d{4})-(\d{2})$/);
  if (!match) return { year: '', month: '' };
  return { year: match[1], month: match[2] };
}

function buildYearMonth(year: string, month: string): string {
  if (!year || !month) return '';
  return `${year}-${month}`;
}

function getYearOptions(): string[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const years: string[] = [''];
  for (let y = currentYear + 1; y >= currentYear - 60; y--) {
    years.push(String(y));
  }
  return years;
}

function getMonthOptions(): string[] {
  return ['', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
}

function YearMonthPicker({
  value,
  onChange,
  ariaLabel,
}: {
  value?: string;
  onChange: (next: string) => void;
  ariaLabel: string;
}) {
  const parts = parseYearMonth(value);
  const [localYear, setLocalYear] = useState(parts.year);
  const [localMonth, setLocalMonth] = useState(parts.month);
  const suppressSyncRef = useRef(false);

  useEffect(() => {
    if (suppressSyncRef.current) {
      suppressSyncRef.current = false;
      return;
    }

    const next = parseYearMonth(value);
    setLocalYear(next.year);
    setLocalMonth(next.month);
  }, [value]);

  const yearOptions = getYearOptions();
  const monthOptions = getMonthOptions();

  return (
    <div className="resumeYearMonthRow" aria-label={ariaLabel}>
      <select
        className="resumeInput"
        value={localYear}
        onChange={(e) => {
          const nextYear = e.target.value;
          setLocalYear(nextYear);

          if (!nextYear) {
            setLocalMonth('');
            onChange('');
            return;
          }

          // If month not chosen yet, clear stored value but keep the user's selection locally.
          if (!localMonth) {
            suppressSyncRef.current = true;
            onChange('');
            return;
          }

          onChange(buildYearMonth(nextYear, localMonth));
        }}
        aria-label={`${ariaLabel} year`}
      >
        {yearOptions.map((y) => (
          <option key={y || 'blank'} value={y}>
            {y || 'Year'}
          </option>
        ))}
      </select>
      <select
        className="resumeInput"
        value={localMonth}
        onChange={(e) => {
          const nextMonth = e.target.value;
          setLocalMonth(nextMonth);

          if (!nextMonth) {
            setLocalYear('');
            onChange('');
            return;
          }

          if (!localYear) {
            suppressSyncRef.current = true;
            onChange('');
            return;
          }

          onChange(buildYearMonth(localYear, nextMonth));
        }}
        aria-label={`${ariaLabel} month`}
      >
        {monthOptions.map((m) => (
          <option key={m || 'blank'} value={m}>
            {m || 'Month'}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Resume() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const [resume, setResume] = useState<ResumeType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [importingPdf, setImportingPdf] = useState(false);
  const [importPdfError, setImportPdfError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [locationStyle, setLocationStyle] = useState<LocationStyle>('keep');
  const [normalizeTarget, setNormalizeTarget] = useState<'experience' | 'education' | null>(null);
  const [normalizeLoading, setNormalizeLoading] = useState(false);
  const [normalizeError, setNormalizeError] = useState('');
  const [normalizedExperiencePreview, setNormalizedExperiencePreview] = useState<ResumeExperience[] | null>(null);
  const [normalizedEducationPreview, setNormalizedEducationPreview] = useState<ResumeEducation[] | null>(null);
  const [checkInSuggestions, setCheckInSuggestions] = useState<Achievement[]>([]);
  const [checkInSuggestionsLoading, setCheckInSuggestionsLoading] = useState(false);
  const [checkInSuggestionsError, setCheckInSuggestionsError] = useState('');
  const [addingSuggestionIndex, setAddingSuggestionIndex] = useState<number | null>(null);

  const loadResume = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Try to load resume with 30 second timeout (Cloud Functions may cold-start)
      const resumePromise = getResume();
      const timeoutPromise = new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), 30000);
      });

      const data = await Promise.race([resumePromise, timeoutPromise]);
      setResume(data);

      if (!data && user) {
        const emptyResumeData: ResumeData = {
          personalInfo: {
            name: user.displayName || '',
            email: user.email || '',
          },
          experience: [],
          education: [],
          skills: {
            technical: [],
            soft: [],
          },
          projects: [],
        };
        setResume({
          id: 'new',
          userId: user.uid,
          data: emptyResumeData,
          version: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        setEditMode(true);
      }
    } catch (err: any) {
      console.error('Failed to load resume:', err);
      setError(err.message || 'Failed to load resume');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const clearNormalizePreview = useCallback(() => {
    setNormalizeTarget(null);
    setNormalizeError('');
    setNormalizedExperiencePreview(null);
    setNormalizedEducationPreview(null);
  }, []);

  const handleNormalize = useCallback(
    async (target: 'experience' | 'education') => {
      if (!resume) return;
      if (!editMode) return;

      setNormalizeTarget(target);
      setNormalizeError('');
      setNormalizeLoading(true);

      try {
        const result = await normalizeResumeSections({
          experience: target === 'experience' ? resume.data.experience || [] : undefined,
          education: target === 'education' ? resume.data.education || [] : undefined,
          locationStyle,
        });

        setNormalizedExperiencePreview(result.normalized.experience || null);
        setNormalizedEducationPreview(result.normalized.education || null);
      } catch (e: any) {
        setNormalizeError(e?.message || 'Failed to normalize');
        setNormalizedExperiencePreview(null);
        setNormalizedEducationPreview(null);
      } finally {
        setNormalizeLoading(false);
      }
    },
    [editMode, locationStyle, resume]
  );

  const handleApplyNormalization = useCallback(async () => {
    if (!resume) return;
    if (!editMode) return;
    if (!normalizeTarget) return;

    const nextData: ResumeData = {
      ...resume.data,
      experience:
        normalizeTarget === 'experience' && normalizedExperiencePreview
          ? normalizedExperiencePreview
          : resume.data.experience,
      education:
        normalizeTarget === 'education' && normalizedEducationPreview
          ? normalizedEducationPreview
          : resume.data.education,
    };

    try {
      setSaving(true);
      setError('');
      setNormalizeError('');

      const saved = await updateResume(nextData);
      setResume(saved);
      clearNormalizePreview();
    } catch (e: any) {
      setNormalizeError(e?.message || 'Failed to apply normalization');
    } finally {
      setSaving(false);
    }
  }, [clearNormalizePreview, editMode, normalizeTarget, normalizedEducationPreview, normalizedExperiencePreview, resume]);

  const countChangedExperience = useCallback(
    (current: ResumeExperience[], preview: ResumeExperience[] | null): number => {
      if (!preview) return 0;
      const len = Math.min(current.length, preview.length);
      let changed = 0;
      for (let i = 0; i < len; i++) {
        const a = current[i];
        const b = preview[i];
        const same =
          (a.company || '') === (b.company || '') &&
          (a.position || '') === (b.position || '') &&
          (a.location || '') === (b.location || '') &&
          (a.startDate || '') === (b.startDate || '') &&
          (a.endDate || '') === (b.endDate || '') &&
          (a.achievements || []).join('\n') === (b.achievements || []).join('\n') &&
          (a.skills || []).join(',') === (b.skills || []).join(',');
        if (!same) changed++;
      }
      return changed;
    },
    []
  );

  const countChangedEducation = useCallback(
    (current: ResumeEducation[], preview: ResumeEducation[] | null): number => {
      if (!preview) return 0;
      const len = Math.min(current.length, preview.length);
      let changed = 0;
      for (let i = 0; i < len; i++) {
        const a = current[i];
        const b = preview[i];
        const same =
          (a.institution || '') === (b.institution || '') &&
          (a.degree || '') === (b.degree || '') &&
          (a.field || '') === (b.field || '') &&
          (a.location || '') === (b.location || '') &&
          (a.startDate || '') === (b.startDate || '') &&
          (a.endDate || '') === (b.endDate || '') &&
          (a.graduationDate || '') === (b.graduationDate || '') &&
          !!a.isCurrentlyAttending === !!b.isCurrentlyAttending &&
          (a.description || '') === (b.description || '');
        if (!same) changed++;
      }
      return changed;
    },
    []
  );

  useEffect(() => {
    loadResume();
  }, [loadResume]);

  const handleSave = async () => {
    if (!resume) return;

    try {
      setSaving(true);
      setError('');
      const nextTechnicalEntries = sanitizeSkillEntries(getSkillEntries('technical'));
      const nextSoftEntries = sanitizeSkillEntries(getSkillEntries('soft'));

      const dataToSave: ResumeData = {
        ...resume.data,
        skills: {
          ...resume.data.skills,
          technicalEntries: nextTechnicalEntries,
          softEntries: nextSoftEntries,
          technical: nextTechnicalEntries.map((e) => e.name),
          soft: nextSoftEntries.map((e) => e.name),
        },
      };

      console.log('[Resume UI] Saving resume:', dataToSave);

      const updated = await updateResume(dataToSave);
      console.log('[Resume UI] Save successful, updated resume:', updated);
      
      setResume(updated);
      setEditMode(false);
    } catch (err: any) {
      console.error('[Resume UI] Failed to save resume:', err);
      const errorMsg = err.message || 'Failed to save resume';
      setError(errorMsg);
      
      // Show more detailed error in console
      if (err.status) {
        console.error('[Resume UI] Error status:', err.status);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePersonalInfo = (field: string, value: string) => {
    if (!resume) return;
    setResume({
      ...resume,
      data: {
        ...resume.data,
        personalInfo: {
          ...resume.data.personalInfo,
          [field]: value,
        },
      },
    });
  };

  const handleUpdateSummary = (value: string) => {
    if (!resume) return;
    setResume({
      ...resume,
      data: {
        ...resume.data,
        summary: value,
      },
    });
  };

  const parseList = (value: string): string[] => {
    const parts = value
      .split(/[,\n]/g)
      .map((s) => s.trim())
      .filter(Boolean);
    return Array.from(new Set(parts));
  };

  const formatList = (items?: string[]): string => {
    return (items || []).join(', ');
  };

  const sanitizeSkillEntries = useCallback((entries: ResumeSkillEntry[]): ResumeSkillEntry[] => {
    const seen = new Set<string>();
    const cleaned: ResumeSkillEntry[] = [];

    for (const entry of entries) {
      const name = (entry.name || '').trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      cleaned.push({
        name,
        proficiency: entry.proficiency || 'Intermediate',
      });
    }
    return cleaned;
  }, []);

  const ensureStructuredSkills = useCallback((current: ResumeType): ResumeType => {
    const skills = current.data.skills;
    const needsTechnical = !skills.technicalEntries && (skills.technical || []).length > 0;
    const needsSoft = !skills.softEntries && (skills.soft || []).length > 0;
    if (!needsTechnical && !needsSoft) return current;

    const nextTechnical = needsTechnical
      ? sanitizeSkillEntries(
          (skills.technical || []).map((name) => ({ name, proficiency: 'Intermediate' as SkillProficiency }))
        )
      : skills.technicalEntries;

    const nextSoft = needsSoft
      ? sanitizeSkillEntries((skills.soft || []).map((name) => ({ name, proficiency: 'Intermediate' as SkillProficiency })))
      : skills.softEntries;

    return {
      ...current,
      data: {
        ...current.data,
        skills: {
          ...skills,
          technicalEntries: nextTechnical,
          softEntries: nextSoft,
          technical: (nextTechnical || []).map((e) => e.name),
          soft: (nextSoft || []).map((e) => e.name),
        },
      },
    };
  }, [sanitizeSkillEntries]);

  useEffect(() => {
    if (!resume) return;
    const next = ensureStructuredSkills(resume);
    if (next !== resume) setResume(next);
  }, [resume, ensureStructuredSkills]);

  type SkillGroup = 'technical' | 'soft';

  const getSkillEntries = (group: SkillGroup): ResumeSkillEntry[] => {
    if (!resume) return [];
    const skills = resume.data.skills;
    const entries = group === 'technical' ? skills.technicalEntries : skills.softEntries;
    if (entries) return entries;

    const legacy = group === 'technical' ? skills.technical : skills.soft;
    return (legacy || []).map((name) => ({ name, proficiency: 'Intermediate' as SkillProficiency }));
  };

  const setSkillEntries = (group: SkillGroup, entries: ResumeSkillEntry[]) => {
    if (!resume) return;
    const cleaned = editMode
      ? entries.map((e) => ({
          name: e.name ?? '',
          proficiency: e.proficiency || 'Intermediate',
        }))
      : sanitizeSkillEntries(entries);
    const nextSkills = {
      ...resume.data.skills,
      technicalEntries:
        group === 'technical' ? cleaned : (resume.data.skills.technicalEntries || getSkillEntries('technical')),
      softEntries: group === 'soft' ? cleaned : (resume.data.skills.softEntries || getSkillEntries('soft')),
    };

    // Keep legacy arrays in sync for compatibility with other parts of the app
    nextSkills.technical = (nextSkills.technicalEntries || []).map((e) => (e.name || '').trim()).filter(Boolean);
    nextSkills.soft = (nextSkills.softEntries || []).map((e) => (e.name || '').trim()).filter(Boolean);

    setResume({
      ...resume,
      data: {
        ...resume.data,
        skills: nextSkills,
      },
    });
  };

  const handleAddSkill = (group: SkillGroup) => {
    const entries = getSkillEntries(group);
    setSkillEntries(group, [...entries, { name: '', proficiency: 'Intermediate' }]);
  };

  const handleUpdateSkillName = (group: SkillGroup, index: number, name: string) => {
    const entries = [...getSkillEntries(group)];
    const current = entries[index] || { name: '', proficiency: 'Intermediate' };
    entries[index] = { ...current, name };
    setSkillEntries(group, entries);
  };

  const handleUpdateSkillProficiency = (group: SkillGroup, index: number, proficiency: SkillProficiency) => {
    const entries = [...getSkillEntries(group)];
    const current = entries[index] || { name: '', proficiency: 'Intermediate' };
    entries[index] = { ...current, proficiency };
    setSkillEntries(group, entries);
  };

  const handleDeleteSkill = (group: SkillGroup, index: number) => {
    const entries = [...getSkillEntries(group)];
    entries.splice(index, 1);
    setSkillEntries(group, entries);
  };

  const handleMoveSkill = (group: SkillGroup, index: number, direction: 'up' | 'down') => {
    const entries = [...getSkillEntries(group)];
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= entries.length) return;
    const temp = entries[index];
    entries[index] = entries[swapWith];
    entries[swapWith] = temp;
    setSkillEntries(group, entries);
  };

  type EditableEducationField =
    | 'institution'
    | 'degree'
    | 'field'
    | 'location'
    | 'startDate'
    | 'endDate'
    | 'isCurrentlyAttending'
    | 'description';

  const handleAddEducation = () => {
    if (!resume) return;

    const next: ResumeEducation = {
      institution: '',
      degree: '',
      field: '',
      startDate: '',
      endDate: '',
      isCurrentlyAttending: false,
      description: '',
      location: '',
    };

    setResume({
      ...resume,
      data: {
        ...resume.data,
        education: [...(resume.data.education || []), next],
      },
    });
  };

  const handleUpdateEducation = (index: number, field: EditableEducationField, value: string) => {
    if (!resume) return;

    const education = [...(resume.data.education || [])];
    const current: ResumeEducation = education[index] || { institution: '', degree: '', field: '' };
    const next: ResumeEducation = {
      ...current,
      [field]: value,
    };

    if (field === 'isCurrentlyAttending') {
      const isChecked = value === 'true';
      next.isCurrentlyAttending = isChecked;
      if (isChecked) {
        next.endDate = '';
        next.graduationDate = '';
      }
    }

    if (field === 'endDate') {
      next.graduationDate = value;
      next.isCurrentlyAttending = false;
    }

    education[index] = next;

    setResume({
      ...resume,
      data: {
        ...resume.data,
        education,
      },
    });
  };

  const handleDeleteEducation = (index: number) => {
    if (!resume) return;

    const education = [...(resume.data.education || [])];
    education.splice(index, 1);

    setResume({
      ...resume,
      data: {
        ...resume.data,
        education,
      },
    });
  };

  const handleMoveEducation = (index: number, direction: 'up' | 'down') => {
    if (!resume) return;
    const education = [...(resume.data.education || [])];
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= education.length) return;

    const temp = education[index];
    education[index] = education[swapWith];
    education[swapWith] = temp;

    setResume({
      ...resume,
      data: {
        ...resume.data,
        education,
      },
    });
  };

  type EditableExperienceField =
    | 'company'
    | 'position'
    | 'location'
    | 'startDate'
    | 'endDate'
    | 'achievements'
    | 'skills';

  const handleAddExperience = () => {
    if (!resume) return;

    const next: ResumeExperience = {
      company: '',
      position: '',
      location: '',
      startDate: '',
      endDate: '',
      achievements: [],
      skills: [],
    };

    setResume({
      ...resume,
      data: {
        ...resume.data,
        experience: [...(resume.data.experience || []), next],
      },
    });
  };

  const handleUpdateExperience = (index: number, field: EditableExperienceField, value: string) => {
    if (!resume) return;

    const experience = [...(resume.data.experience || [])];
    const current: ResumeExperience =
      experience[index] || ({ company: '', position: '', startDate: '', achievements: [] } as ResumeExperience);

    const next: ResumeExperience = { ...current };

    if (field === 'achievements') {
      next.achievements = value
        .split(/\n/g)
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (field === 'skills') {
      next.skills = parseList(value);
    } else {
      (next as any)[field] = value;
    }

    // Normalize optional empty strings
    if (next.location === '') delete (next as any).location;
    if (next.endDate === '') delete (next as any).endDate;
    if ((next.skills || []).length === 0) delete (next as any).skills;

    experience[index] = next;

    setResume({
      ...resume,
      data: {
        ...resume.data,
        experience,
      },
    });
  };

  const handleDeleteExperience = (index: number) => {
    if (!resume) return;
    const experience = [...(resume.data.experience || [])];
    experience.splice(index, 1);
    setResume({
      ...resume,
      data: {
        ...resume.data,
        experience,
      },
    });
  };

  const handleMoveExperience = (index: number, direction: 'up' | 'down') => {
    if (!resume) return;
    const experience = [...(resume.data.experience || [])];
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (swapWith < 0 || swapWith >= experience.length) return;

    const temp = experience[index];
    experience[index] = experience[swapWith];
    experience[swapWith] = temp;

    setResume({
      ...resume,
      data: {
        ...resume.data,
        experience,
      },
    });
  };

  type EditableProjectField =
    | 'name'
    | 'description'
    | 'url'
    | 'startDate'
    | 'endDate'
    | 'technologies'
    | 'achievements';

  const handleAddProject = () => {
    if (!resume) return;
    const next: ResumeProject = {
      name: '',
      description: '',
      technologies: [],
      achievements: [],
      url: '',
      startDate: '',
      endDate: '',
    };

    setResume({
      ...resume,
      data: {
        ...resume.data,
        projects: [...(resume.data.projects || []), next],
      },
    });
  };

  const handleUpdateProject = (index: number, field: EditableProjectField, value: string) => {
    if (!resume) return;
    const projects = [...(resume.data.projects || [])];
    const current: ResumeProject =
      projects[index] || ({ name: '', description: '', technologies: [], achievements: [] } as ResumeProject);

    const next: ResumeProject = { ...current };

    if (field === 'technologies') {
      next.technologies = parseList(value);
    } else if (field === 'achievements') {
      next.achievements = value
        .split(/\n/g)
        .map((s) => s.trim())
        .filter(Boolean);
    } else {
      (next as any)[field] = value;
    }

    // Normalize optional empty strings
    if (next.url === '') delete (next as any).url;
    if (next.startDate === '') delete (next as any).startDate;
    if (next.endDate === '') delete (next as any).endDate;

    projects[index] = next;

    setResume({
      ...resume,
      data: {
        ...resume.data,
        projects,
      },
    });
  };

  const handleDeleteProject = (index: number) => {
    if (!resume) return;
    const projects = [...(resume.data.projects || [])];
    projects.splice(index, 1);
    setResume({
      ...resume,
      data: {
        ...resume.data,
        projects,
      },
    });
  };

  type EditableCertField = 'name' | 'issuer' | 'issuedDate' | 'expiryDate' | 'credentialId' | 'url';

  const handleAddCertification = () => {
    if (!resume) return;
    setResume({
      ...resume,
      data: {
        ...resume.data,
        certifications: [...(resume.data.certifications || []), { name: '' }],
      },
    });
  };

  const handleUpdateCertification = (index: number, field: EditableCertField, value: string) => {
    if (!resume) return;
    const certs = (resume.data.certifications || []).map((c) =>
      typeof c === 'string' ? { name: c } : { ...c }
    );
    (certs[index] as any)[field] = value;
    // Strip empty optional fields
    for (const key of ['issuer', 'issuedDate', 'expiryDate', 'credentialId', 'url'] as const) {
      if ((certs[index] as any)[key] === '') delete (certs[index] as any)[key];
    }
    setResume({ ...resume, data: { ...resume.data, certifications: certs } });
  };

  const handleDeleteCertification = (index: number) => {
    if (!resume) return;
    const certs = [...(resume.data.certifications || [])];
    certs.splice(index, 1);
    setResume({ ...resume, data: { ...resume.data, certifications: certs } });
  };

  const handleExportPDF = () => {
    if (!resume) return;
    exportResumeToPDF(resume);
  };

  const handlePickPdf = () => {
    if (saving || importingPdf) return;
    setImportPdfError('');
    fileInputRef.current?.click();
  };

  const handlePdfSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file later.
    e.target.value = '';

    if (!file) return;
    if (!resume) return;

    try {
      setImportingPdf(true);
      setImportPdfError('');

      const result = await importResumeFromPdf(file, { language });

      setResume({
        ...resume,
        data: result.resumeData,
        updatedAt: new Date().toISOString(),
      });
      setEditMode(true);
    } catch (err: any) {
      console.error('PDF import failed:', err);
      setImportPdfError(err?.message || 'Failed to import resume from PDF');
    } finally {
      setImportingPdf(false);
    }
  };

  const handleUpdateFromCheckIns = async () => {
    try {
      setCheckInSuggestionsLoading(true);
      setCheckInSuggestionsError('');
      const extraction = await extractConversationAchievements();
      setCheckInSuggestions(extraction.achievements || []);
      if (!extraction.achievements || extraction.achievements.length === 0) {
        setCheckInSuggestionsError(extraction.message || 'No check-in achievements found yet.');
      }
    } catch (err: any) {
      console.error('Failed to extract achievements from check-ins:', err);
      setCheckInSuggestions([]);
      setCheckInSuggestionsError(err?.message || 'Failed to load check-in suggestions.');
    } finally {
      setCheckInSuggestionsLoading(false);
    }
  };

  const handleAddSuggestionToResume = async (suggestion: Achievement, index: number) => {
    if (!resume) return;

    try {
      setAddingSuggestionIndex(index);
      setError('');

      const nextExperience = [...(resume.data.experience || [])];
      const suggestedCompany = (suggestion.suggestedCompany || '').trim();
      const suggestedPosition = (suggestion.suggestedPosition || '').trim();
      const normalizedCompany = suggestedCompany.toLowerCase();
      const normalizedPosition = suggestedPosition.toLowerCase();

      const targetIndex = nextExperience.findIndex((exp) => {
        if (!normalizedCompany || !normalizedPosition) return false;
        return (exp.company || '').trim().toLowerCase() === normalizedCompany
          && (exp.position || '').trim().toLowerCase() === normalizedPosition;
      });

      if (targetIndex >= 0) {
        const target = { ...nextExperience[targetIndex] };
        const currentAchievements = [...(target.achievements || [])];
        const alreadyExists = currentAchievements.some(
          (achievement) => achievement.trim().toLowerCase() === suggestion.text.trim().toLowerCase()
        );

        if (!alreadyExists) {
          currentAchievements.push(suggestion.text.trim());
        }

        target.achievements = currentAchievements;
        nextExperience[targetIndex] = target;
      } else {
        nextExperience.push({
          company: suggestedCompany,
          position: suggestedPosition || 'Achievement',
          location: '',
          startDate: '',
          endDate: '',
          achievements: [suggestion.text.trim()],
          skills: suggestion.category === 'skill' ? [suggestion.text.trim()] : [],
        });
      }

      const nextData: ResumeData = {
        ...resume.data,
        experience: nextExperience,
      };

      const updated = await updateResume(nextData);
      setResume(updated);
      setCheckInSuggestions((prev) => prev.filter((_, i) => i !== index));
    } catch (err: any) {
      console.error('Failed to add suggestion to resume:', err);
      setError(err?.message || 'Failed to add suggestion to resume');
    } finally {
      setAddingSuggestionIndex(null);
    }
  };

  const handleApplyCritiqueSuggestion = async (suggestion: ResumeCritiqueSuggestion): Promise<void> => {
    if (!resume) return;
    setError('');

    const nextData: ResumeData = {
        ...resume.data,
        experience: [...(resume.data.experience || [])],
        education: [...(resume.data.education || [])],
        skills: {
          technical: [...(resume.data.skills?.technical || [])],
          soft: [...(resume.data.skills?.soft || [])],
          technicalEntries: [...(resume.data.skills?.technicalEntries || [])],
          softEntries: [...(resume.data.skills?.softEntries || [])],
          languages: [...(resume.data.skills?.languages || [])],
          certifications: [...(resume.data.skills?.certifications || [])],
        },
        projects: [...(resume.data.projects || [])],
      };

      if (suggestion.sectionAffected === 'summary') {
        nextData.summary = suggestion.proposedChange;
      } else if (suggestion.sectionAffected === 'skills') {
        // proposedChange may be a comma-separated list of skills (e.g. "React, Docker, AWS")
        const incoming = suggestion.proposedChange
          .split(/[,;]+/)
          .map((s) => s.trim())
          .filter(Boolean);
        const existingLower = new Set(nextData.skills.technical.map((s) => s.trim().toLowerCase()));
        for (const skill of incoming) {
          if (!existingLower.has(skill.toLowerCase())) {
            nextData.skills.technical.push(skill);
            existingLower.add(skill.toLowerCase());
          }
        }
      } else {
        const text = suggestion.proposedChange.trim();
        if (text) {
          if (!nextData.experience.length) {
            nextData.experience.push({
              company: '',
              position: 'Achievement',
              location: '',
              startDate: '',
              endDate: '',
              achievements: [text],
              skills: [],
            });
          } else {
            const target = { ...nextData.experience[0] };
            const achievements = [...(target.achievements || [])];
            if (!achievements.some((a) => a.trim().toLowerCase() === text.toLowerCase())) {
              achievements.push(text);
            }
            target.achievements = achievements;
            nextData.experience[0] = target;
          }
        }
      }

      const updated = await updateResume(nextData);
      setResume(updated);
  };

  if (loading) {
    return (
      <div className="resumeCard">
        {/* Personal Info skeleton */}
        <div className="resumeSkeletonSection">
          <div className="resumeSkeletonLine resumeSkeletonHeader" />
          <div className="resumeSkeletonRow">
            <div className="resumeSkeletonLine resumeSkeletonField" />
            <div className="resumeSkeletonLine resumeSkeletonField" />
          </div>
          <div className="resumeSkeletonRow">
            <div className="resumeSkeletonLine resumeSkeletonField" />
            <div className="resumeSkeletonLine resumeSkeletonFieldShort" />
          </div>
        </div>
        {/* Summary skeleton */}
        <div className="resumeSkeletonSection">
          <div className="resumeSkeletonLine resumeSkeletonHeader resumeSkeletonW20" />
          <div className="resumeSkeletonLine resumeSkeletonBlock" />
        </div>
        {/* Experience skeleton */}
        <div className="resumeSkeletonSection">
          <div className="resumeSkeletonLine resumeSkeletonHeader resumeSkeletonW25" />
          <div className="resumeSkeletonRow">
            <div className="resumeSkeletonLine resumeSkeletonField" />
            <div className="resumeSkeletonLine resumeSkeletonFieldShort" />
          </div>
          <div className="resumeSkeletonLine resumeSkeletonSmall" />
          <div className="resumeSkeletonLine resumeSkeletonBlock" />
        </div>
        {/* Skills skeleton */}
        <div className="resumeSkeletonSection">
          <div className="resumeSkeletonLine resumeSkeletonHeader resumeSkeletonW15" />
          <div className="resumeSkeletonRow">
            <div className="resumeSkeletonLine resumeSkeletonField" />
            <div className="resumeSkeletonLine resumeSkeletonField" />
            <div className="resumeSkeletonLine resumeSkeletonField" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="resumeCard">
        <p className="resumeErrorText">{error}</p>
        <button
          onClick={loadResume}
          className="resumeButton resumeButtonPrimary"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!resume) {
    return (
      <div className="resumeCard resumeCentered">
        <p className="resumeInfoText">No resume found</p>
      </div>
    );
  }

  return (
    <div className="resumeCard">
      <div className="resumeHeader">
        <h2 className="resumeTitle">My Resume</h2>
        <div className="resumeButtonRow">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handlePdfSelected}
            aria-label="Import resume PDF"
            className="resumeHiddenInput"
          />

          {editMode ? (
            <>
              <button
                onClick={handlePickPdf}
                disabled={importingPdf || saving}
                className="resumeButton resumeButtonSecondary"
              >
                {importingPdf ? 'Importing…' : 'Import from PDF'}
              </button>
              <button
                onClick={() => navigate('/resume-history')}
                disabled={saving || importingPdf}
                className="resumeButton resumeButtonSecondary"
              >
                Resume History
              </button>
              <button
                onClick={() => navigate('/saved-resumes')}
                disabled={saving || importingPdf}
                className="resumeButton resumeButtonSecondary"
              >
                Saved Versions
              </button>
              <button
                onClick={() => setEditMode(false)}
                disabled={saving}
                className="resumeButton resumeButtonSecondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="resumeButton resumeButtonPrimary"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handlePickPdf}
                disabled={importingPdf}
                className="resumeButton resumeButtonSecondary"
              >
                {importingPdf ? 'Importing…' : 'Import from PDF'}
              </button>
              <button
                onClick={() => navigate('/resume-history')}
                disabled={importingPdf}
                className="resumeButton resumeButtonSecondary"
              >
                Resume History
              </button>
              <button
                onClick={() => navigate('/saved-resumes')}
                disabled={importingPdf}
                className="resumeButton resumeButtonSecondary"
              >
                Saved Versions
              </button>
              <button
                onClick={handleExportPDF}
                className="resumeButton resumeButtonSuccess"
              >
                Export to PDF
              </button>
              <button
                onClick={() => setEditMode(true)}
                className="resumeButton resumeButtonPrimary"
              >
                Edit Resume
              </button>
            </>
          )}
        </div>
      </div>

      {importPdfError && (
        <div className="resumeSection">
          <p className="resumeErrorText">{importPdfError}</p>
        </div>
      )}

      <div className="resumeSection">
        <h3 className="resumeSectionTitle">Personal Information</h3>
        {editMode ? (
          <div className="resumeFormStack">
            <input
              type="text"
              placeholder="Full Name"
              value={resume.data.personalInfo.name}
              onChange={(e) => handleUpdatePersonalInfo('name', e.target.value)}
              className="resumeInput"
            />
            <input
              type="email"
              placeholder="Email"
              value={resume.data.personalInfo.email}
              onChange={(e) => handleUpdatePersonalInfo('email', e.target.value)}
              className="resumeInput"
            />
            <input
              type="tel"
              placeholder="Phone"
              value={resume.data.personalInfo.phone || ''}
              onChange={(e) => handleUpdatePersonalInfo('phone', e.target.value)}
              className="resumeInput"
            />
            <input
              type="text"
              placeholder="Location"
              value={resume.data.personalInfo.location || ''}
              onChange={(e) => handleUpdatePersonalInfo('location', e.target.value)}
              className="resumeInput"
            />
          </div>
        ) : (
          <div className="resumeInfoText">
            <p><strong>Name:</strong> {resume.data.personalInfo.name}</p>
            <p><strong>Email:</strong> {resume.data.personalInfo.email}</p>
            {resume.data.personalInfo.phone && (
              <p><strong>Phone:</strong> {resume.data.personalInfo.phone}</p>
            )}
            {resume.data.personalInfo.location && (
              <p><strong>Location:</strong> {resume.data.personalInfo.location}</p>
            )}
          </div>
        )}
      </div>

      <div className="resumeSection">
        <h3 className="resumeSectionTitle">Professional Summary</h3>
        {editMode ? (
          <textarea
            placeholder="Write a brief professional summary..."
            value={resume.data.summary || ''}
            onChange={(e) => handleUpdateSummary(e.target.value)}
            rows={4}
            className="resumeTextarea"
          />
        ) : (
          <p className={resume.data.summary ? 'resumeBodyText' : 'resumeMutedText'}>
            {resume.data.summary || 'No summary added yet'}
          </p>
        )}
      </div>

      <ResumeCritique
        onApplySuggestion={handleApplyCritiqueSuggestion}
        disabled={saving || addingSuggestionIndex !== null}
      />

      <div className="resumeSection">
        <h3 className="resumeSectionTitle">Experience</h3>
        <div className="resumeSectionActions">
          <button
            onClick={handleUpdateFromCheckIns}
            className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
            disabled={checkInSuggestionsLoading || saving || addingSuggestionIndex !== null}
            data-testid="resume-update-from-checkins"
          >
            {checkInSuggestionsLoading ? 'Loading check-ins…' : 'Update from Check-ins'}
          </button>
        </div>

        {(checkInSuggestions.length > 0 || checkInSuggestionsError) && (
          <div className="resumeSuggestionPanel" data-testid="resume-checkin-suggestions">
            {checkInSuggestionsError && <p className="resumeErrorText resumeNoMargin">{checkInSuggestionsError}</p>}

            {checkInSuggestions.length > 0 && (
              <>
                <p className="resumeInfoText resumeNoMargin">Suggested updates from your latest check-in:</p>
                <div className="resumeListStack resumeTopMarginSmall">
                  {checkInSuggestions.map((suggestion, index) => (
                    <div className="resumeItemCard" key={`${suggestion.text}-${index}`}>
                      <p className="resumeBodyText resumeNoMargin">{suggestion.text}</p>
                      {(suggestion.suggestedPosition || suggestion.suggestedCompany) && (
                        <p className="resumeMetaText resumeNoMargin">
                          {suggestion.suggestedPosition || 'Achievement'}
                          {suggestion.suggestedCompany ? ` • ${suggestion.suggestedCompany}` : ''}
                        </p>
                      )}
                      {suggestion.sourceConversationTitle && (
                        <p className="resumeMetaText resumeNoMargin">Source: {suggestion.sourceConversationTitle}</p>
                      )}
                      <div className="resumeTopMarginSmall">
                        <button
                          className="resumeButton resumeButtonPrimary resumeButtonSmall"
                          onClick={() => handleAddSuggestionToResume(suggestion, index)}
                          disabled={saving || addingSuggestionIndex === index}
                          data-testid={`resume-add-suggestion-${index}`}
                        >
                          {addingSuggestionIndex === index ? 'Adding…' : 'Add to Resume'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {editMode && (
          <div className="resumeSectionActions">
            <select
              className="resumeInput resumeNormalizeSelect"
              value={locationStyle}
              onChange={(e) => setLocationStyle(e.target.value as LocationStyle)}
              aria-label="Normalization location format"
            >
              <option value="keep">Location: keep as-is</option>
              <option value="us">Location: US (City, ST)</option>
              <option value="international">Location: International (City, Country)</option>
            </select>
            <button
              onClick={() => handleNormalize('experience')}
              className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
              disabled={normalizeLoading || saving}
            >
              {normalizeLoading && normalizeTarget === 'experience' ? 'Normalizing…' : 'Normalize with AI'}
            </button>
            <button
              onClick={handleAddExperience}
              className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
            >
              + Add Experience
            </button>
          </div>
        )}

        {editMode && normalizeTarget === 'experience' && (normalizedExperiencePreview || normalizeError) && (
          <div className="resumeNormalizePanel">
            <div className="resumeNormalizeHeader">
              <p className="resumeInfoText resumeNoMargin">
                Preview changes: {countChangedExperience(resume.data.experience || [], normalizedExperiencePreview)} item(s) updated.
              </p>
              <div className="resumeButtonRow">
                <button
                  onClick={handleApplyNormalization}
                  className="resumeButton resumeButtonPrimary resumeButtonSmall"
                  disabled={saving || normalizeLoading || !normalizedExperiencePreview}
                >
                  Apply
                </button>
                <button
                  onClick={clearNormalizePreview}
                  className="resumeButton resumeButtonSecondary resumeButtonSmall"
                  disabled={saving || normalizeLoading}
                >
                  Cancel
                </button>
              </div>
            </div>

            {normalizeError && <p className="resumeErrorText">{normalizeError}</p>}

            {normalizedExperiencePreview && (
              <div className="resumeDiffGrid">
                <div className="resumeDiffHeader">Current</div>
                <div className="resumeDiffHeader">Normalized</div>
                {(resume.data.experience || []).map((exp, idx) => {
                  const next = normalizedExperiencePreview[idx];
                  if (!next) return null;
                  const left = `${exp.position || ''} @ ${exp.company || ''}${exp.location ? ` • ${exp.location}` : ''}\n${exp.startDate || ''} - ${exp.endDate || 'Present'}`;
                  const right = `${next.position || ''} @ ${next.company || ''}${next.location ? ` • ${next.location}` : ''}\n${next.startDate || ''} - ${next.endDate || 'Present'}`;
                  if (left === right && (exp.achievements || []).join('\n') === (next.achievements || []).join('\n')) return null;
                  return (
                    <>
                      <div key={`exp-old-${idx}`} className="resumeDiffCell">
                        <p className="resumeBodyText resumeNoMargin">{left}</p>
                        {(exp.achievements || []).length > 0 && (
                          <p className="resumeMetaText resumeNoMargin">{(exp.achievements || []).slice(0, 3).join(' • ')}</p>
                        )}
                      </div>
                      <div key={`exp-new-${idx}`} className="resumeDiffCell">
                        <p className="resumeBodyText resumeNoMargin">{right}</p>
                        {(next.achievements || []).length > 0 && (
                          <p className="resumeMetaText resumeNoMargin">{(next.achievements || []).slice(0, 3).join(' • ')}</p>
                        )}
                      </div>
                    </>
                  );
                })}
              </div>
            )}

            <p className="resumeMetaText resumeNoMargin">Applying saves a new resume version.</p>
          </div>
        )}

        {(resume.data.experience || []).length === 0 ? (
          <p className="resumeMutedText">
            No experience added yet. Achievements from your weekly check-ins will appear here automatically.
          </p>
        ) : editMode ? (
          <div className="resumeListStack">
            {(resume.data.experience || []).map((exp, index) => (
              <div key={index} className="resumeItemCard">
                <div className="resumeItemHeader">
                  <h4 className="resumeItemHeaderTitle">Experience #{index + 1}</h4>
                  <div className="resumeButtonRow">
                    <button
                      onClick={() => handleMoveExperience(index, 'up')}
                      disabled={index === 0}
                      className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
                    >
                      Up
                    </button>
                    <button
                      onClick={() => handleMoveExperience(index, 'down')}
                      disabled={index === (resume.data.experience || []).length - 1}
                      className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
                    >
                      Down
                    </button>
                    <button
                      onClick={() => handleDeleteExperience(index)}
                      className="resumeButton resumeButtonOutlineDanger resumeButtonSmall"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="resumeGrid2">
                  <input
                    type="text"
                    placeholder="Position"
                    value={exp.position || ''}
                    onChange={(e) => handleUpdateExperience(index, 'position', e.target.value)}
                    className="resumeInput"
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    value={exp.company || ''}
                    onChange={(e) => handleUpdateExperience(index, 'company', e.target.value)}
                    className="resumeInput"
                  />
                  <input
                    type="text"
                    placeholder="Location (optional)"
                    value={exp.location || ''}
                    onChange={(e) => handleUpdateExperience(index, 'location', e.target.value)}
                    className="resumeInput"
                  />
                  <input
                    type="text"
                    placeholder="Skills (comma-separated, optional)"
                    value={formatList(exp.skills)}
                    onChange={(e) => handleUpdateExperience(index, 'skills', e.target.value)}
                    className="resumeInput"
                  />
                  <YearMonthPicker
                    ariaLabel="Experience start date"
                    value={exp.startDate || ''}
                    onChange={(next) => handleUpdateExperience(index, 'startDate', next)}
                  />
                  <YearMonthPicker
                    ariaLabel="Experience end date (optional)"
                    value={exp.endDate || ''}
                    onChange={(next) => handleUpdateExperience(index, 'endDate', next)}
                  />
                </div>

                <textarea
                  placeholder="Achievements (one per line)"
                  value={(exp.achievements || []).join('\n')}
                  onChange={(e) => handleUpdateExperience(index, 'achievements', e.target.value)}
                  rows={4}
                  className="resumeTextarea resumeTextareaTopMargin"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="resumeListStack">
            {(resume.data.experience || []).map((exp, index) => (
              <div key={index} className="resumeItemCard">
                <h4 className="resumeItemTitle">{exp.position}</h4>
                <p className="resumeSubText">
                  {exp.company} {exp.location && `• ${exp.location}`}
                </p>
                <p className="resumeMetaText">
                  {exp.startDate} - {exp.endDate || 'Present'}
                </p>
                {(exp.achievements || []).length > 0 && (
                  <ul className="resumeBulletList">
                    {(exp.achievements || []).map((achievement, i) => (
                      <li key={i} className="resumeBulletItem">
                        {achievement}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="resumeSection">
        <h3 className="resumeSectionTitle">Education</h3>
        {editMode && (
          <div className="resumeSectionActions">
            <select
              className="resumeInput resumeNormalizeSelect"
              value={locationStyle}
              onChange={(e) => setLocationStyle(e.target.value as LocationStyle)}
              aria-label="Normalization location format"
            >
              <option value="keep">Location: keep as-is</option>
              <option value="us">Location: US (City, ST)</option>
              <option value="international">Location: International (City, Country)</option>
            </select>
            <button
              onClick={() => handleNormalize('education')}
              className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
              disabled={normalizeLoading || saving}
            >
              {normalizeLoading && normalizeTarget === 'education' ? 'Normalizing…' : 'Normalize with AI'}
            </button>
            <button
              onClick={handleAddEducation}
              className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
            >
              + Add Education
            </button>
          </div>
        )}

        {editMode && normalizeTarget === 'education' && (normalizedEducationPreview || normalizeError) && (
          <div className="resumeNormalizePanel">
            <div className="resumeNormalizeHeader">
              <p className="resumeInfoText resumeNoMargin">
                Preview changes: {countChangedEducation(resume.data.education || [], normalizedEducationPreview)} item(s) updated.
              </p>
              <div className="resumeButtonRow">
                <button
                  onClick={handleApplyNormalization}
                  className="resumeButton resumeButtonPrimary resumeButtonSmall"
                  disabled={saving || normalizeLoading || !normalizedEducationPreview}
                >
                  Apply
                </button>
                <button
                  onClick={clearNormalizePreview}
                  className="resumeButton resumeButtonSecondary resumeButtonSmall"
                  disabled={saving || normalizeLoading}
                >
                  Cancel
                </button>
              </div>
            </div>

            {normalizeError && <p className="resumeErrorText">{normalizeError}</p>}

            {normalizedEducationPreview && (
              <div className="resumeDiffGrid">
                <div className="resumeDiffHeader">Current</div>
                <div className="resumeDiffHeader">Normalized</div>
                {(resume.data.education || []).map((edu, idx) => {
                  const next = normalizedEducationPreview[idx];
                  if (!next) return null;
                  const left = `${edu.degree || ''}${edu.field ? ` in ${edu.field}` : ''}\n${edu.institution || ''}${edu.location ? ` • ${edu.location}` : ''}`;
                  const right = `${next.degree || ''}${next.field ? ` in ${next.field}` : ''}\n${next.institution || ''}${next.location ? ` • ${next.location}` : ''}`;
                  if (left === right && (edu.description || '') === (next.description || '')) return null;
                  return (
                    <>
                      <div key={`edu-old-${idx}`} className="resumeDiffCell">
                        <p className="resumeBodyText resumeNoMargin">{left}</p>
                        {edu.description && (
                          <p className="resumeMetaText resumeNoMargin">{edu.description}</p>
                        )}
                      </div>
                      <div key={`edu-new-${idx}`} className="resumeDiffCell">
                        <p className="resumeBodyText resumeNoMargin">{right}</p>
                        {next.description && (
                          <p className="resumeMetaText resumeNoMargin">{next.description}</p>
                        )}
                      </div>
                    </>
                  );
                })}
              </div>
            )}

            <p className="resumeMetaText resumeNoMargin">Applying saves a new resume version.</p>
          </div>
        )}

        {(resume.data.education || []).length === 0 ? (
          <p className="resumeMutedText">No education added yet</p>
        ) : editMode ? (
          <div className="resumeListStack">
            {(resume.data.education || []).map((edu, index) => (
              <div
                key={index}
                className="resumeItemCard"
              >
                <div className="resumeItemHeader">
                  <h4 className="resumeItemHeaderTitle">Education #{index + 1}</h4>
                  <div className="resumeButtonRow">
                    <button
                      onClick={() => handleMoveEducation(index, 'up')}
                      disabled={index === 0}
                      className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
                    >
                      Up
                    </button>
                    <button
                      onClick={() => handleMoveEducation(index, 'down')}
                      disabled={index === (resume.data.education || []).length - 1}
                      className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
                    >
                      Down
                    </button>
                    <button
                      onClick={() => handleDeleteEducation(index)}
                      className="resumeButton resumeButtonOutlineDanger resumeButtonSmall"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="resumeGrid2">
                  <input
                    type="text"
                    placeholder="Institution"
                    value={edu.institution || ''}
                    onChange={(e) => handleUpdateEducation(index, 'institution', e.target.value)}
                    className="resumeInput"
                  />
                  <input
                    type="text"
                    placeholder="Location (optional)"
                    value={edu.location || ''}
                    onChange={(e) => handleUpdateEducation(index, 'location', e.target.value)}
                    className="resumeInput"
                  />
                  <input
                    type="text"
                    placeholder="Degree"
                    value={edu.degree || ''}
                    onChange={(e) => handleUpdateEducation(index, 'degree', e.target.value)}
                    className="resumeInput"
                  />
                  <input
                    type="text"
                    placeholder="Field of study"
                    value={edu.field || ''}
                    onChange={(e) => handleUpdateEducation(index, 'field', e.target.value)}
                    className="resumeInput"
                  />
                  <YearMonthPicker
                    ariaLabel="Education start date"
                    value={edu.startDate || ''}
                    onChange={(next) => handleUpdateEducation(index, 'startDate', next)}
                  />
                  {edu.isCurrentlyAttending ? (
                    <div className="resumeInfoText">
                      <p className="resumeDateLabel">End date: Present</p>
                    </div>
                  ) : (
                    <YearMonthPicker
                      ariaLabel="Education end date"
                      value={edu.endDate || edu.graduationDate || ''}
                      onChange={(next) => handleUpdateEducation(index, 'endDate', next)}
                    />
                  )}
                </div>

                <label className="resumeCheckboxRow">
                  <input
                    type="checkbox"
                    checked={!!edu.isCurrentlyAttending}
                    onChange={(e) => handleUpdateEducation(index, 'isCurrentlyAttending', String(e.target.checked))}
                  />
                  <span className="resumeBodyText">Currently attending</span>
                </label>

                <textarea
                  placeholder="Description / Notes (optional)"
                  value={edu.description || ''}
                  onChange={(e) => handleUpdateEducation(index, 'description', e.target.value)}
                  rows={3}
                  className="resumeTextarea resumeTextareaTopMargin"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="resumeListStack">
            {resume.data.education.map((edu, index) => {
              const startDate = edu.startDate;
              const endDate = edu.isCurrentlyAttending ? 'Present' : (edu.endDate || edu.graduationDate);
              const hasRange = !!startDate || !!endDate;
              const dateLabel = hasRange
                ? `${startDate || ''}${startDate && endDate ? ' - ' : ''}${endDate || ''}`
                : '';

              return (
                <div
                  key={index}
                  className="resumeItemCard"
                >
                  <h4 className="resumeItemTitle">
                    {edu.degree}{edu.field ? ` in ${edu.field}` : ''}
                  </h4>
                  <p className="resumeSubText resumeNoMargin">
                    {edu.institution}
                    {edu.location ? ` • ${edu.location}` : ''}
                  </p>
                  {dateLabel && (
                    <p className="resumeDateLabel">{dateLabel}</p>
                  )}
                  {edu.description && (
                    <p className="resumeDescription">
                      {edu.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="resumeSection">
        <h3 className="resumeSectionTitle">Skills</h3>
        {editMode ? (
          <div className="resumeListStack">
            <div className="resumeItemCard">
              <div className="resumeItemHeader">
                <h4 className="resumeItemHeaderTitle">Technical Skills</h4>
                <button
                  onClick={() => handleAddSkill('technical')}
                  className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
                >
                  + Add Skill
                </button>
              </div>

              {(getSkillEntries('technical') || []).length === 0 ? (
                <p className="resumeMutedText">No technical skills added yet</p>
              ) : (
                <div className="resumeListStack">
                  {getSkillEntries('technical').map((skill, index) => (
                    <div key={index} className="resumeItemCard">
                      <div className="resumeItemHeader">
                        <h4 className="resumeItemHeaderTitle">Skill #{index + 1}</h4>
                        <div className="resumeButtonRow">
                          <button
                            onClick={() => handleMoveSkill('technical', index, 'up')}
                            disabled={index === 0}
                            className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
                          >
                            Up
                          </button>
                          <button
                            onClick={() => handleMoveSkill('technical', index, 'down')}
                            disabled={index === getSkillEntries('technical').length - 1}
                            className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
                          >
                            Down
                          </button>
                          <button
                            onClick={() => handleDeleteSkill('technical', index)}
                            className="resumeButton resumeButtonOutlineDanger resumeButtonSmall"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="resumeGrid2">
                        <input
                          type="text"
                          placeholder="Skill name"
                          value={skill.name || ''}
                          onChange={(e) => handleUpdateSkillName('technical', index, e.target.value)}
                          className="resumeInput"
                        />
                        <select
                          className="resumeInput"
                          value={skill.proficiency || 'Intermediate'}
                          onChange={(e) => handleUpdateSkillProficiency('technical', index, e.target.value as SkillProficiency)}
                          aria-label="Technical skill proficiency"
                        >
                          <option value="Beginner">Beginner</option>
                          <option value="Intermediate">Intermediate</option>
                          <option value="Advanced">Advanced</option>
                          <option value="Expert">Expert</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="resumeItemCard">
              <div className="resumeItemHeader">
                <h4 className="resumeItemHeaderTitle">Soft Skills</h4>
                <button
                  onClick={() => handleAddSkill('soft')}
                  className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
                >
                  + Add Skill
                </button>
              </div>

              {(getSkillEntries('soft') || []).length === 0 ? (
                <p className="resumeMutedText">No soft skills added yet</p>
              ) : (
                <div className="resumeListStack">
                  {getSkillEntries('soft').map((skill, index) => (
                    <div key={index} className="resumeItemCard">
                      <div className="resumeItemHeader">
                        <h4 className="resumeItemHeaderTitle">Skill #{index + 1}</h4>
                        <div className="resumeButtonRow">
                          <button
                            onClick={() => handleMoveSkill('soft', index, 'up')}
                            disabled={index === 0}
                            className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
                          >
                            Up
                          </button>
                          <button
                            onClick={() => handleMoveSkill('soft', index, 'down')}
                            disabled={index === getSkillEntries('soft').length - 1}
                            className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
                          >
                            Down
                          </button>
                          <button
                            onClick={() => handleDeleteSkill('soft', index)}
                            className="resumeButton resumeButtonOutlineDanger resumeButtonSmall"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="resumeGrid2">
                        <input
                          type="text"
                          placeholder="Skill name"
                          value={skill.name || ''}
                          onChange={(e) => handleUpdateSkillName('soft', index, e.target.value)}
                          className="resumeInput"
                        />
                        <select
                          className="resumeInput"
                          value={skill.proficiency || 'Intermediate'}
                          onChange={(e) => handleUpdateSkillProficiency('soft', index, e.target.value as SkillProficiency)}
                          aria-label="Soft skill proficiency"
                        >
                          <option value="Beginner">Beginner</option>
                          <option value="Intermediate">Intermediate</option>
                          <option value="Advanced">Advanced</option>
                          <option value="Expert">Expert</option>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) :
        (resume.data.skills.technicalEntries || []).length === 0 &&
          (resume.data.skills.softEntries || []).length === 0 &&
          resume.data.skills.technical.length === 0 &&
          resume.data.skills.soft.length === 0 ? (
          <p className="resumeMutedText">No skills added yet</p>
        ) : (
          <div>
            {(resume.data.skills.technicalEntries || []).length > 0 ? (
              <div className="resumeSkillGroup">
                <strong className="resumeStrong">Technical:</strong>
                <p className="resumeInfoText">
                  {(resume.data.skills.technicalEntries || []).map((s) => `${s.name} (${s.proficiency})`).join(', ')}
                </p>
              </div>
            ) : resume.data.skills.technical.length > 0 ? (
              <div className="resumeSkillGroup">
                <strong className="resumeStrong">Technical:</strong>
                <p className="resumeInfoText">{resume.data.skills.technical.join(', ')}</p>
              </div>
            ) : null}

            {(resume.data.skills.softEntries || []).length > 0 ? (
              <div>
                <strong className="resumeStrong">Soft Skills:</strong>
                <p className="resumeInfoText">
                  {(resume.data.skills.softEntries || []).map((s) => `${s.name} (${s.proficiency})`).join(', ')}
                </p>
              </div>
            ) : resume.data.skills.soft.length > 0 ? (
              <div>
                <strong className="resumeStrong">Soft Skills:</strong>
                <p className="resumeInfoText">{resume.data.skills.soft.join(', ')}</p>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="resumeSection">
        <h3 className="resumeSectionTitle">Projects</h3>
        {editMode && (
          <div className="resumeSectionActions">
            <button
              onClick={handleAddProject}
              className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
            >
              + Add Project
            </button>
          </div>
        )}

        {(resume.data.projects || []).length === 0 ? (
          <p className="resumeMutedText">No projects added yet</p>
        ) : editMode ? (
          <div className="resumeListStack">
            {(resume.data.projects || []).map((project, index) => (
              <div key={index} className="resumeItemCard">
                <div className="resumeItemHeader">
                  <h4 className="resumeItemHeaderTitle">Project #{index + 1}</h4>
                  <button
                    onClick={() => handleDeleteProject(index)}
                    className="resumeButton resumeButtonOutlineDanger resumeButtonSmall"
                  >
                    Delete
                  </button>
                </div>

                <div className="resumeGrid2">
                  <input
                    type="text"
                    placeholder="Project name"
                    value={project.name || ''}
                    onChange={(e) => handleUpdateProject(index, 'name', e.target.value)}
                    className="resumeInput"
                  />
                  <input
                    type="text"
                    placeholder="URL (optional)"
                    value={project.url || ''}
                    onChange={(e) => handleUpdateProject(index, 'url', e.target.value)}
                    className="resumeInput"
                  />
                  <input
                    type="text"
                    placeholder="Start date (YYYY-MM)"
                    value={project.startDate || ''}
                    onChange={(e) => handleUpdateProject(index, 'startDate', e.target.value)}
                    className="resumeInput"
                  />
                  <input
                    type="text"
                    placeholder="End date (YYYY-MM)"
                    value={project.endDate || ''}
                    onChange={(e) => handleUpdateProject(index, 'endDate', e.target.value)}
                    className="resumeInput"
                  />
                </div>

                <textarea
                  placeholder="Description"
                  value={project.description || ''}
                  onChange={(e) => handleUpdateProject(index, 'description', e.target.value)}
                  rows={3}
                  className="resumeTextarea resumeTextareaTopMargin"
                />

                <textarea
                  placeholder="Technologies (comma-separated)"
                  value={formatList(project.technologies)}
                  onChange={(e) => handleUpdateProject(index, 'technologies', e.target.value)}
                  rows={2}
                  className="resumeTextarea resumeTextareaTopMargin"
                />

                <textarea
                  placeholder="Achievements (one per line)"
                  value={(project.achievements || []).join('\n')}
                  onChange={(e) => handleUpdateProject(index, 'achievements', e.target.value)}
                  rows={3}
                  className="resumeTextarea resumeTextareaTopMargin"
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="resumeListStack">
            {(resume.data.projects || []).map((project, index) => {
              const hasRange = !!project.startDate || !!project.endDate;
              const dateLabel = hasRange
                ? `${project.startDate || ''}${project.startDate && project.endDate ? ' - ' : ''}${project.endDate || ''}`
                : '';

              return (
                <div key={index} className="resumeItemCard">
                  <h4 className="resumeItemTitle">{project.name}</h4>
                  {project.url && (
                    <p className="resumeSubText">
                      <a href={project.url} target="_blank" rel="noreferrer">
                        {project.url}
                      </a>
                    </p>
                  )}
                  {dateLabel && <p className="resumeDateLabel">{dateLabel}</p>}
                  {project.description && <p className="resumeBodyText">{project.description}</p>}
                  {project.technologies?.length ? (
                    <p className="resumeInfoText">
                      <strong>Tech:</strong> {project.technologies.join(', ')}
                    </p>
                  ) : null}
                  {project.achievements?.length ? (
                    <ul className="resumeBulletList">
                      {project.achievements.map((a, i) => (
                        <li key={i} className="resumeBulletItem">
                          {a}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Certifications */}
      {(editMode || (resume.data.certifications?.length ?? 0) > 0) && (
        <div className="resumeSection">
          <h3 className="resumeSectionTitle">Licenses &amp; Certifications</h3>
          {editMode && (
            <div className="resumeSectionActions">
              <button
                type="button"
                onClick={handleAddCertification}
                className="resumeButton resumeButtonOutlinePrimary resumeButtonSmall"
              >
                + Add Certification
              </button>
            </div>
          )}
          {editMode ? (
            <div className="resumeListStack">
              {(resume.data.certifications || []).map((cert, index) => {
                const c = typeof cert === 'string' ? { name: cert as string } : cert;
                return (
                  <div key={index} className="resumeItemCard">
                    <div className="resumeItemHeader">
                      <h4 className="resumeItemHeaderTitle">Certification #{index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => handleDeleteCertification(index)}
                        className="resumeButton resumeButtonOutlineDanger resumeButtonSmall"
                      >
                        Delete
                      </button>
                    </div>
                    <div className="resumeGrid2">
                      <input
                        type="text"
                        placeholder="Certification name"
                        value={c.name || ''}
                        onChange={(e) => handleUpdateCertification(index, 'name', e.target.value)}
                        className="resumeInput"
                      />
                      <input
                        type="text"
                        placeholder="Issuing organization"
                        value={c.issuer || ''}
                        onChange={(e) => handleUpdateCertification(index, 'issuer', e.target.value)}
                        className="resumeInput"
                      />
                      <input
                        type="text"
                        placeholder="Issue date (YYYY-MM or YYYY)"
                        value={c.issuedDate || ''}
                        onChange={(e) => handleUpdateCertification(index, 'issuedDate', e.target.value)}
                        className="resumeInput"
                      />
                      <input
                        type="text"
                        placeholder="Expiry date (YYYY-MM or YYYY)"
                        value={c.expiryDate || ''}
                        onChange={(e) => handleUpdateCertification(index, 'expiryDate', e.target.value)}
                        className="resumeInput"
                      />
                      <input
                        type="text"
                        placeholder="Credential ID (optional)"
                        value={c.credentialId || ''}
                        onChange={(e) => handleUpdateCertification(index, 'credentialId', e.target.value)}
                        className="resumeInput"
                      />
                      <input
                        type="text"
                        placeholder="Credential URL (optional)"
                        value={c.url || ''}
                        onChange={(e) => handleUpdateCertification(index, 'url', e.target.value)}
                        className="resumeInput"
                      />
                    </div>
                  </div>
                );
              })}
              {(resume.data.certifications || []).length === 0 && (
                <p className="resumeMutedText">No certifications added yet</p>
              )}
            </div>
          ) : (
            <div className="resumeItemList">
              {(resume.data.certifications || []).map((cert, index) => {
                const c = typeof cert === 'string' ? { name: cert as string } : cert;
                return (
                  <div key={index} className="resumeItemCard">
                    <h4 className="resumeItemTitle">{c.name}</h4>
                    {c.issuer && <p className="resumeSubText">{c.issuer}</p>}
                    {(c.issuedDate || c.expiryDate) && (
                      <p className="resumeDateLabel">
                        {c.issuedDate ? `Issued ${c.issuedDate}` : ''}
                        {c.issuedDate && c.expiryDate ? ' · ' : ''}
                        {c.expiryDate ? `Expires ${c.expiryDate}` : ''}
                      </p>
                    )}
                    {c.credentialId && (
                      <p className="resumeInfoText">ID: {c.credentialId}</p>
                    )}
                    {c.url && (
                      <p className="resumeInfoText">
                        <a href={c.url} target="_blank" rel="noreferrer">Show credential</a>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="resumeFooter">
        <p className="resumeFooterText">
          Version {resume.version} • Last updated {new Date(resume.updatedAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}
